import { useState, type FC } from 'react';
import { FileText, Folder } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { fetchDownloadSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import { useOptionalIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { previewMimeType } from 'components/IAP/actionTicket/documentPreview';

export type NotificationAttachedFile = {
  fileId: string;
  name: string;
  kind: 'file' | 'folder';
  fileUrl: string | null;
  mimetype: string | null;
};

type Props = {
  files?: NotificationAttachedFile[] | null;
};

/**
 * Expanded-inbox list of File Manager attachments on a notification.
 * Files open via signed URL; folders are listed by name (browse in File Manager).
 */
const NotificationAttachedFiles: FC<Props> = ({ files }) => {
  const preview = useOptionalIcsFormPdfPreview();
  const [openingId, setOpeningId] = useState<string | null>(null);

  if (!files?.length) return null;

  const openFile = async (file: NotificationAttachedFile) => {
    if (file.kind === 'folder' || !file.fileUrl) {
      toast.info('Open this folder from File Manager.');
      return;
    }
    setOpeningId(file.fileId);
    try {
      const signed = await fetchDownloadSignedUrl(file.fileUrl);
      if (preview) {
        preview.previewFromSignedUrl({
          fileUrl: signed,
          fileName: file.name,
          mimeType: previewMimeType(file.name, file.mimetype),
          onDownload: () => {
            window.open(signed, '_blank', 'noopener,noreferrer');
          },
        });
      } else {
        window.open(signed, '_blank', 'noopener,noreferrer');
      }
    } catch {
      toast.error('Unable to open attachment.');
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className="tw:space-y-1.5" data-tour="notifications-attached-files">
      <p className="tw:m-0 tw:text-[11px] tw:font-medium tw:text-muted-foreground">
        Attachments
      </p>
      <ul className="tw:m-0 tw:list-none tw:space-y-1 tw:p-0">
        {files.map((file) => (
          <li
            key={file.fileId}
            className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:bg-muted/40 tw:px-2 tw:py-1.5"
          >
            {file.kind === 'folder' ? (
              <Folder className="tw:size-3.5 tw:shrink-0 tw:text-muted-foreground" />
            ) : (
              <FileText className="tw:size-3.5 tw:shrink-0 tw:text-muted-foreground" />
            )}
            <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-xs tw:text-foreground">
              {file.name}
            </span>
            {file.kind === 'file' && file.fileUrl ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                disabled={openingId === file.fileId}
                onClick={(event) => {
                  event.stopPropagation();
                  void openFile(file);
                }}
              >
                Open
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default NotificationAttachedFiles;
